import { useState } from 'react'
import type { Cohort, AgeBracket, EducationLevel, IncomeLevel } from '@/types'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { IdeologyMini } from '@/components/ideology/IdeologyDisplay'
import { SalienceMini } from '@/components/ideology/SalienceDisplay'
import { CohortDetail } from './CohortDetail'
import { cn } from '@/lib/utils'

interface CohortCardProps {
  cohort: Cohort
  className?: string
}

const ageBracketLabels: Record<AgeBracket, string> = {
  youth: 'Youth',
  young_adult: 'Young Adult',
  middle_aged: 'Middle Aged',
  senior: 'Senior',
}

const educationLevelLabels: Record<EducationLevel, string> = {
  low: 'Low Ed',
  medium: 'Med Ed',
  high: 'High Ed',
}

const incomeLevelLabels: Record<IncomeLevel, string> = {
  low: 'Low Income',
  middle: 'Middle Income',
  high: 'High Income',
}

function formatPopulation(population: number): string {
  return population.toLocaleString()
}

function formatTurnout(turnout: number): string {
  return `${Math.round(turnout * 100)}%`
}

/**
 * Compact card view of a single cohort.
 * Shows demographics, population, turnout, and expandable details.
 */
export function CohortCard({ cohort, className }: CohortCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const demographicString = `${ageBracketLabels[cohort.age_bracket]} • ${educationLevelLabels[cohort.education_level]} • ${incomeLevelLabels[cohort.income_level]}`

  return (
    <Card className={cn('transition-all', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <CardTitle className="text-base">{demographicString}</CardTitle>
            <CardDescription className="flex items-center gap-3">
              <span>Pop: {formatPopulation(cohort.population)}</span>
              <span className="text-muted-foreground">|</span>
              <span>Turnout: {formatTurnout(cohort.base_turnout)}</span>
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {!isExpanded && (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Ideology:</span>
              <IdeologyMini ideology={cohort.ideology_vector} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Priorities:</span>
              <SalienceMini salience={cohort.salience_weights} count={2} />
            </div>
          </div>
        )}
        {isExpanded && (
          <CohortDetail cohort={cohort} />
        )}
      </CardContent>
    </Card>
  )
}

/**
 * More compact inline cohort display for tables/lists
 */
interface CohortRowProps {
  cohort: Cohort
  onClick?: () => void
  className?: string
}

export function CohortRow({ cohort, onClick, className }: CohortRowProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <div>
          <div className="font-medium text-sm">
            {ageBracketLabels[cohort.age_bracket]} • {educationLevelLabels[cohort.education_level]} • {incomeLevelLabels[cohort.income_level]}
          </div>
          <div className="text-xs text-muted-foreground">
            Pop: {formatPopulation(cohort.population)} | Turnout: {formatTurnout(cohort.base_turnout)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <IdeologyMini ideology={cohort.ideology_vector} />
        {onClick && (
          <span className="text-xs text-muted-foreground">View details</span>
        )}
      </div>
    </div>
  )
}
